import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { KeyIcon, XIcon, AlertCircleIcon } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import { useAPIKey } from '../../contexts/APIKeyContext';
import { useNavigate } from 'react-router-dom';

interface BetaAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BetaAccessModal: React.FC<BetaAccessModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { setApiKey, validateKey } = useAPIKey();
  const [inputKey, setInputKey] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsValidating(true);
    
    if (!validateKey(inputKey)) {
      setError('Please enter a valid OpenAI API key');
      setIsValidating(false);
      return;
    }

    // Store API key using context
    setApiKey(inputKey);
    
    // Navigate to app after a brief delay to allow context to update
    setTimeout(() => {
      setIsValidating(false);
      onClose();
      navigate('/app');
    }, 500);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
     role="dialog"
     aria-modal="true"
     aria-labelledby="beta-modal-title"
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            <KeyIcon className="h-6 w-6 text-primary-600 mr-2" />
            <h2 id="beta-modal-title" className="text-xl font-semibold text-gray-800">VoiceVision Beta Access</h2>
          </div>
          <button
           aria-label="Close modal"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-primary-50 border-l-4 border-primary-500 p-4 rounded-r-lg mb-4">
            <p className="text-primary-800">
              VoiceVision supports <strong>BYOK (Bring Your Own Key)</strong> for unlimited access.
              Your OpenAI API key enables unlimited sessions, removes daily limits, and gives you 
              complete control over your AI usage and billing.
            </p>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
            <h4 className="font-medium text-blue-800 mb-2">BYOK Benefits:</h4>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• Unlimited session time (no daily limits)</li>
              <li>• Direct billing through your OpenAI account</li>
              <li>• Works with any subscription plan (including Free)</li>
              <li>• Complete control over your AI usage</li>
              <li>• Access to latest OpenAI models and features</li>
            </ul>
          </div>
          
          {error && (
            <div className="bg-error-50 border-l-4 border-error-500 p-4 rounded-r-lg mb-4">
              <div className="flex items-center text-error-800">
                <AlertCircleIcon className="h-5 w-5 mr-2" />
                <p>{error}</p>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <Input
           id="api-key-input"
            label="OpenAI API Key"
            type="password"
            value={inputKey}
            onChange={(e) => setInputKey(e.target.value)}
            placeholder="sk-..."
            leftIcon={<KeyIcon className="h-5 w-5 text-gray-400" />}
            helper="Your API key is stored locally and never sent to our servers"
            required
          />

          <div className="mt-6 flex flex-col gap-3">
            <Button
             aria-label={isValidating ? 'Validating API key, please wait' : 'Continue to workspace with API key'}
              type="submit"
              variant="primary"
              isLoading={isValidating}
              fullWidth
              className="bg-gradient-to-r from-secondary-500 to-primary-500 hover:from-secondary-600 hover:to-primary-600"
            >
              {isValidating ? 'Validating...' : 'Continue to Workspace'}
            </Button>
            
            <Button
             aria-label="Cancel and close modal"
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default BetaAccessModal;